import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './UserManagement.css';
import { getUsers, createUser, updateUser, deleteUser, exportUsers } from '../../services/usersService';

const mapUserFromAPI = (apiUser = {}) => {
  const statusFromApi = apiUser.status || apiUser.user_status;
  let derivedStatus = statusFromApi;

  if (!derivedStatus && typeof apiUser.is_active === 'boolean') {
    derivedStatus = apiUser.is_active ? 'Active' : 'Inactive';
  }

  return {
    id: apiUser.user_id || apiUser.id?.toString() || apiUser.uuid || '',
    userId: apiUser.user_id || apiUser.id?.toString() || apiUser.uuid || '',
    fullName: apiUser.full_name || apiUser.name || apiUser.fullName || '',
    email: apiUser.email || '',
    mobileNumber: apiUser.mobile_number || apiUser.mobile || apiUser.phone || '',
    role: apiUser.role || apiUser.user_role || apiUser.type || '',
    status: derivedStatus || 'Active',
    lastLogin: apiUser.last_login || apiUser.lastLogin || '',
  };
};

const normalizeUsersResponse = (responsePayload = {}) => {
  const payload = responsePayload?.data ?? responsePayload;

  const candidates = [
    payload?.users,
    payload?.items,
    payload?.results,
    payload?.data,
    payload,
  ];

  let usersList = [];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      usersList = candidate;
      break;
    }

    if (Array.isArray(candidate?.items)) {
      usersList = candidate.items;
      break;
    }
  }

  const total =
    payload?.total ||
    payload?.totalCount ||
    payload?.count ||
    payload?.pagination?.total ||
    payload?.meta?.total ||
    usersList.length;

  return {
    users: (usersList || []).map(mapUserFromAPI),
    total: typeof total === 'number' ? total : usersList.length,
  };
};

const UserManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [showEntriesDropdownOpen, setShowEntriesDropdownOpen] = useState(false);
  const [roleSearch, setRoleSearch] = useState('');
  const [statusSearch, setStatusSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [formData, setFormData] = useState({
    userId: '',
    fullName: '',
    email: '',
    mobileNumber: '',
    role: 'Bill Manager',
    status: 'Active',
    password: '',
    confirmPassword: '',
    photo: null,
    photoPreview: null
  });
  const [formErrors, setFormErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successUserName, setSuccessUserName] = useState('');
  const [successModalType, setSuccessModalType] = useState('add');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [deletedUserInfo, setDeletedUserInfo] = useState(null);

  const roleDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const showEntriesDropdownRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Restrict available roles to Bill Manager only (backend requires role='Bill Manager')
  const availableRoles = useMemo(() => ['Bill Manager'], []);

  const availableStatuses = ['Active', 'Inactive'];

  const totalPages = Math.max(1, Math.ceil(Math.max(totalUsers, 1) / itemsPerPage));
  const paginatedUsers = users;
  const startEntry = paginatedUsers.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endEntry = (currentPage - 1) * itemsPerPage + paginatedUsers.length;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target)) {
        setRoleDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
      if (showEntriesDropdownRef.current && !showEntriesDropdownRef.current.contains(event.target)) {
        setShowEntriesDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, roleFilter, statusFilter, itemsPerPage]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await getUsers({
        page: currentPage,
        limit: itemsPerPage,
        search: searchQuery || undefined,
        role: roleFilter || undefined,
        status: statusFilter || undefined,
      });

      const { users: normalizedUsers, total } = normalizeUsersResponse(response);
      setUsers(normalizedUsers);
      setTotalUsers(total);
    } catch (err) {
      setError(err.message || 'Failed to fetch users. Please try again.');
      setUsers([]);
      setTotalUsers(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchQuery, roleFilter, statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Auto-hide success modals
  useEffect(() => {
    if (showSuccessModal) {
      const timer = setTimeout(() => {
        setShowSuccessModal(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessModal]);

  useEffect(() => {
    if (showDeleteSuccess) {
      const timer = setTimeout(() => {
        setShowDeleteSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showDeleteSuccess]);

  const clearPaging = () => setCurrentPage(1);

  const handleApplyFilters = () => {
    clearPaging();
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setRoleFilter('');
    setStatusFilter('');
    clearPaging();
  };

  // Generate auto User ID
  const generateUserId = () => {
    const existingIds = users.map(u => {
      const match = u.userId.match(/USR-(\d+)/);
      return match ? parseInt(match[1]) : 0;
    });
    const nextId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;
    return `USR-${String(nextId).padStart(3, '0')}`;
  };

  const handleAddUser = () => {
    const newId = generateUserId();
    setFormData({
      userId: newId,
      fullName: '',
      email: '',
      mobileNumber: '',
      role: 'Bill Manager',
      status: 'Active',
      password: '',
      confirmPassword: '',
      photo: null,
      photoPreview: null
    });
    setFormErrors({});
    setShowAddModal(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      userId: user.userId,
      fullName: user.fullName,
      email: user.email,
      mobileNumber: user.mobileNumber,
      role: user.role,
      status: user.status,
      password: '',
      confirmPassword: '',
      photo: null,
      photoPreview: null
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const handleViewUser = (user) => {
    setViewingUser(user);
    setShowViewModal(true);
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        e.target.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          photo: file,
          photoPreview: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.fullName.trim()) {
      errors.fullName = 'Full Name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    
    if (!formData.mobileNumber.trim()) {
      errors.mobileNumber = 'Mobile Number is required';
    } else if (!/^[0-9]{10}$/.test(formData.mobileNumber.replace(/\s+/g, ''))) {
      errors.mobileNumber = 'Mobile Number must be 10 digits';
    }
    
    if (!formData.role) {
      errors.role = 'Role is required';
    }
    
    if (!formData.status) {
      errors.status = 'Status is required';
    }
    
    // Password validation only for new users
    if (showAddModal) {
      if (!formData.password) {
        errors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        errors.password = 'Password must be at least 6 characters';
      }
      
      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    } else if (formData.password && formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    } else if (formData.password && formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    // Check for duplicate email (excluding current user)
    const existingUser = users.find(u => 
      u.email.toLowerCase() === formData.email.toLowerCase() && 
      u.id !== editingUser?.id
    );
    if (existingUser) {
      errors.email = 'Email already exists';
    }
    
    // Check for duplicate mobile (excluding current user)
    const existingMobile = users.find(u => 
      u.mobileNumber === formData.mobileNumber && 
      u.id !== editingUser?.id
    );
    if (existingMobile) {
      errors.mobileNumber = 'Mobile Number already exists';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveUser = async () => {
    if (!validateForm()) return;
    setLoading(true);
    setError('');
    try {
      const mapRoleToUserType = (role) => {
        if (!role) return 'Customer';
        const r = role.toString().toLowerCase();
        if (r.includes('admin')) return 'Admin';
        if (r.includes('agency')) return 'Agency';
        if (r.includes('customer')) return 'Customer';
        if (r.includes('supervisor')) return 'Supervisor';
        if (r.includes('bill') || r.includes('bill manager')) return 'Bill Manager';
        // default fallback to role label if it already matches backend enum, else 'Customer'
        const allowed = ['Admin', 'Agency', 'Customer', 'Supervisor', 'Bill Manager'];
        if (allowed.includes(role)) return role;
        return 'Customer';
      };

      const payload = {
        user_id: formData.userId,
        full_name: formData.fullName,
        email: formData.email,
        mobile_number: formData.mobileNumber,
        role: formData.role,
        user_type: mapRoleToUserType(formData.role),
        status: formData.status,
      };

      // Only include password when provided (create or update)
      if (formData.password && formData.password.trim() !== '') {
        payload.password = formData.password;
      }

      if (showAddModal) {
        await createUser(payload);
        setSuccessUserName(formData.fullName);
        setSuccessModalType('add');
      } else if (showEditModal && editingUser) {
        await updateUser(editingUser.id, payload);
        setSuccessUserName(formData.fullName);
        setSuccessModalType('update');
      }

      // Refresh list from server
      await fetchUsers();

      // Close modals and show success
      setShowAddModal(false);
      setShowEditModal(false);
      setShowSuccessModal(true);
      setEditingUser(null);
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err.message || 'Failed to save user. Please try again.');
    } finally {
      setLoading(false);
      // Reset form
      setFormData({
        userId: '',
        fullName: '',
        email: '',
        mobileNumber: '',
        role: '',
        status: 'Active',
        password: '',
        confirmPassword: '',
        photo: null,
        photoPreview: null
      });
      setFormErrors({});
    }
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteUser = () => {
    if (!userToDelete) return;
    const performDelete = async () => {
      setLoading(true);
      setError('');
      try {
        await deleteUser(userToDelete.id);
        setDeletedUserInfo(userToDelete);
        setShowDeleteConfirm(false);
        setShowDeleteSuccess(true);
        setUserToDelete(null);
        // Refresh from server
        await fetchUsers();
      } catch (err) {
        console.error('Error deleting user:', err);
        setError(err.message || 'Failed to delete user. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    performDelete();
  };

  const handleExport = () => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        // Try server export first
        const resp = await exportUsers();
        // If server returned a blob (csv), trigger download
        if (resp instanceof Blob) {
          const url = URL.createObjectURL(resp);
          const a = document.createElement('a');
          a.href = url;
          a.download = `Users_Export_${new Date().toISOString().split('T')[0]}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } else {
          // resp is likely data payload - normalize and build CSV client-side
          const { users: exportList } = normalizeUsersResponse(resp);
          const headers = ['User ID', 'Full Name', 'Email', 'Mobile Number', 'Role', 'Status', 'Last Login'];
          const escapeCSV = (str) => {
            if (str === null || str === undefined) return '';
            const s = String(str);
            if (s.includes(',') || s.includes('"') || s.includes('\n')) {
              return '"' + s.replace(/"/g, '""') + '"';
            }
            return s;
          };
          const rows = [headers.map(escapeCSV)];
          exportList.forEach(u => {
            rows.push([
              u.userId || '',
              u.fullName || '',
              u.email || '',
              u.mobileNumber || '',
              u.role || '',
              u.status || '',
              u.lastLogin || ''
            ].map(escapeCSV));
          });
          const csv = '\ufeff' + rows.map(r => r.join(',')).join('\n');
          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `Users_Export_${new Date().toISOString().split('T')[0]}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      } catch (err) {
        console.error('Export users failed:', err);
        setError(err.message || 'Failed to export users. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  };

  // Filter options for dropdowns
  const filteredRoles = availableRoles.filter(role =>
    role.toLowerCase().includes(roleSearch.toLowerCase())
  );

  const filteredStatuses = availableStatuses.filter(status =>
    status.toLowerCase().includes(statusSearch.toLowerCase())
  );

  return (
    <div className="user-management-container">
      {/* Header */}
      <div className="user-header">
        <div className="filter-group">
          <div className="filter-wrapper" ref={roleDropdownRef}>
            <button
              className={`filter-btn ${roleFilter ? 'active' : ''}`}
              onClick={() => setRoleDropdownOpen(!roleDropdownOpen)}
            >
              <span>Role: {roleFilter || 'All'}</span>
              <span>▼</span>
            </button>
            {roleDropdownOpen && (
              <div className="filter-dropdown">
                <input
                  type="text"
                  placeholder="Search role..."
                  className="filter-search-input"
                  value={roleSearch}
                  onChange={(e) => setRoleSearch(e.target.value)}
                />
                <button
                  className={`filter-option ${!roleFilter ? 'selected' : ''}`}
                  onClick={() => {
                    setRoleFilter('');
                    setRoleDropdownOpen(false);
                    setRoleSearch('');
                  }}
                >
                  All
                </button>
                {filteredRoles.map(role => (
                  <button
                    key={role}
                    className={`filter-option ${roleFilter === role ? 'selected' : ''}`}
                    onClick={() => {
                      setRoleFilter(role);
                      setRoleDropdownOpen(false);
                      setRoleSearch('');
                    }}
                  >
                    {role}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="filter-wrapper" ref={statusDropdownRef}>
            <button
              className={`filter-btn ${statusFilter ? 'active' : ''}`}
              onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
            >
              <span>Status: {statusFilter || 'All'}</span>
              <span>▼</span>
            </button>
            {statusDropdownOpen && (
              <div className="filter-dropdown">
                <input
                  type="text"
                  placeholder="Search status..."
                  className="filter-search-input"
                  value={statusSearch}
                  onChange={(e) => setStatusSearch(e.target.value)}
                />
                <button
                  className={`filter-option ${!statusFilter ? 'selected' : ''}`}
                  onClick={() => {
                    setStatusFilter('');
                    setStatusDropdownOpen(false);
                    setStatusSearch('');
                  }}
                >
                  All
                </button>
                {filteredStatuses.map(status => (
                  <button
                    key={status}
                    className={`filter-option ${statusFilter === status ? 'selected' : ''}`}
                    onClick={() => {
                      setStatusFilter(status);
                      setStatusDropdownOpen(false);
                      setStatusSearch('');
                    }}
                  >
                    {status}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button className="btn-reset" onClick={handleResetFilters}>
            Reset Filters
          </button>
        </div>
        <div className="user-actions">
          <button className="btn-icon-only" onClick={handleExport} title="Export">
            <span>📤</span>
          </button>
          <button className="btn-primary" onClick={handleAddUser}>
            <span>➕</span> Add User
          </button>
        </div>
      </div>

      <div className="show-entries-container">
        <div className="show-entries-wrapper" style={{ width: '100%' }}>
          <div className="show-entries-left" ref={showEntriesDropdownRef}>
            <label className="show-entries-label">Show</label>
            <button
              className="show-entries-btn"
              onClick={() => setShowEntriesDropdownOpen(!showEntriesDropdownOpen)}
            >
              <span>{itemsPerPage}</span>
              <span>▼</span>
            </button>
            {showEntriesDropdownOpen && (
              <div className="show-entries-dropdown">
                {[10, 25, 50, 100].map(num => (
                  <button
                    key={num}
                    className={`show-entries-option ${itemsPerPage === num ? 'selected' : ''}`}
                    onClick={() => {
                      setItemsPerPage(num);
                      setShowEntriesDropdownOpen(false);
                      setCurrentPage(1);
                    }}
                  >
                    {num}
                  </button>
                ))}
              </div>
            )}
            <label className="show-entries-label">entries</label>
          </div>
          <div className="search-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search by name, email, mobile, or user ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span className="search-icon">🔍</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="user-table-wrapper">
        <table className="user-table">
          <thead>
            <tr>
              <th>User ID</th>
              <th>Full Name</th>
              <th>Email</th>
              <th>Mobile Number</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="no-data">
                  Loading users...
                </td>
              </tr>
            ) : paginatedUsers.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">
                  {error ? 'Unable to load users' : 'No users found'}
                </td>
              </tr>
            ) : (
              paginatedUsers.map(user => (
                <tr key={user.id || user.userId}>
                  <td>{user.userId}</td>
                  <td>{user.fullName}</td>
                  <td>{user.email}</td>
                  <td>{user.mobileNumber}</td>
                  <td>
                    <span className={`role-badge role-${(user.role || 'unknown').toLowerCase().replace(/\s+/g, '-')}`}>
                      {user.role || 'N/A'}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge status-${(user.status || 'inactive').toLowerCase()}`}>
                      {user.status || 'Inactive'}
                    </span>
                  </td>
                  <td>{user.lastLogin || '—'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-view"
                        onClick={() => handleViewUser(user)}
                        title="View"
                      >
                        👁️
                      </button>
                      <button
                        className="btn-edit"
                        onClick={() => handleEditUser(user)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteUser(user)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination-wrapper">
        <div className="pagination-info">
          <div className="pagination-count">
          Showing {totalUsers === 0 ? 0 : startEntry} to {totalUsers === 0 ? 0 : Math.min(endEntry, totalUsers)} of {totalUsers} entries
          </div>
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1100px', width: '90%' }}>
            <div className="modal-header">
              <h2>Add New User</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                <div className="form-group">
                  <label>User ID <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.userId}
                    disabled
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Full Name <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    className={`form-input ${formErrors.fullName ? 'error' : ''}`}
                  />
                  {formErrors.fullName && <span className="error-message">{formErrors.fullName}</span>}
                </div>
                <div className="form-group">
                  <label>Email <span className="required">*</span></label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`form-input ${formErrors.email ? 'error' : ''}`}
                  />
                  {formErrors.email && <span className="error-message">{formErrors.email}</span>}
                </div>
                <div className="form-group">
                  <label>Mobile Number <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.mobileNumber}
                    onChange={(e) => setFormData({ ...formData, mobileNumber: e.target.value })}
                    className={`form-input ${formErrors.mobileNumber ? 'error' : ''}`}
                    maxLength="10"
                  />
                  {formErrors.mobileNumber && <span className="error-message">{formErrors.mobileNumber}</span>}
                </div>
                <div className="form-group">
                  <label>Role <span className="required">*</span></label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className={`form-input ${formErrors.role ? 'error' : ''}`}
                  >
                    <option value="">Select Role</option>
                    {availableRoles.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                  {formErrors.role && <span className="error-message">{formErrors.role}</span>}
                </div>
                <div className="form-group">
                  <label>Status <span className="required">*</span></label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className={`form-input ${formErrors.status ? 'error' : ''}`}
                  >
                    {availableStatuses.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                  {formErrors.status && <span className="error-message">{formErrors.status}</span>}
                </div>
                <div className="form-group">
                  <label>Password <span className="required">*</span></label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className={`form-input ${formErrors.password ? 'error' : ''}`}
                  />
                  {formErrors.password && <span className="error-message">{formErrors.password}</span>}
                </div>
                <div className="form-group">
                  <label>Confirm Password <span className="required">*</span></label>
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className={`form-input ${formErrors.confirmPassword ? 'error' : ''}`}
                  />
                  {formErrors.confirmPassword && <span className="error-message">{formErrors.confirmPassword}</span>}
                </div>
                <div className="form-group">
                  <label>Photo</label>
                  <div className="photo-upload-wrapper">
                    {formData.photoPreview ? (
                      <div className="photo-preview">
                        <img src={formData.photoPreview} alt="Preview" />
                        <button
                          type="button"
                          className="remove-photo"
                          onClick={() => setFormData({ ...formData, photo: null, photoPreview: null })}
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <label className="photo-upload-label">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          style={{ display: 'none' }}
                        />
                        <span>📷 Upload Photo</span>
                      </label>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button className="btn-save" onClick={handleSaveUser}>
                Save User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1100px', width: '90%' }}>
            <div className="modal-header">
              <h2>Edit User</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                <div className="form-group">
                  <label>User ID</label>
                  <input
                    type="text"
                    value={formData.userId}
                    disabled
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Full Name <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    className={`form-input ${formErrors.fullName ? 'error' : ''}`}
                  />
                  {formErrors.fullName && <span className="error-message">{formErrors.fullName}</span>}
                </div>
                <div className="form-group">
                  <label>Email <span className="required">*</span></label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`form-input ${formErrors.email ? 'error' : ''}`}
                  />
                  {formErrors.email && <span className="error-message">{formErrors.email}</span>}
                </div>
                <div className="form-group">
                  <label>Mobile Number <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.mobileNumber}
                    onChange={(e) => setFormData({ ...formData, mobileNumber: e.target.value })}
                    className={`form-input ${formErrors.mobileNumber ? 'error' : ''}`}
                    maxLength="10"
                  />
                  {formErrors.mobileNumber && <span className="error-message">{formErrors.mobileNumber}</span>}
                </div>
                <div className="form-group">
                  <label>Role <span className="required">*</span></label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className={`form-input ${formErrors.role ? 'error' : ''}`}
                  >
                    <option value="">Select Role</option>
                    {availableRoles.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                  {formErrors.role && <span className="error-message">{formErrors.role}</span>}
                </div>
                <div className="form-group">
                  <label>Status <span className="required">*</span></label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className={`form-input ${formErrors.status ? 'error' : ''}`}
                  >
                    {availableStatuses.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                  {formErrors.status && <span className="error-message">{formErrors.status}</span>}
                </div>
                <div className="form-group">
                  <label>New Password (leave blank to keep current)</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className={`form-input ${formErrors.password ? 'error' : ''}`}
                    placeholder="Enter new password"
                  />
                  {formErrors.password && <span className="error-message">{formErrors.password}</span>}
                </div>
                {formData.password && (
                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <input
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className={`form-input ${formErrors.confirmPassword ? 'error' : ''}`}
                      placeholder="Confirm new password"
                    />
                    {formErrors.confirmPassword && <span className="error-message">{formErrors.confirmPassword}</span>}
                  </div>
                )}
                <div className="form-group">
                  <label>Photo</label>
                  <div className="photo-upload-wrapper">
                    {formData.photoPreview ? (
                      <div className="photo-preview">
                        <img src={formData.photoPreview} alt="Preview" />
                        <button
                          type="button"
                          className="remove-photo"
                          onClick={() => setFormData({ ...formData, photo: null, photoPreview: null })}
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <label className="photo-upload-label">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          style={{ display: 'none' }}
                        />
                        <span>📷 Upload Photo</span>
                      </label>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
              <button className="btn-save" onClick={handleSaveUser}>
                Update User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View User Modal */}
      {showViewModal && viewingUser && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>User Details</h2>
              <button className="modal-close" onClick={() => setShowViewModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="view-details">
                <div className="detail-row">
                  <span className="detail-label">User ID:</span>
                  <span className="detail-value">{viewingUser.userId}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Full Name:</span>
                  <span className="detail-value">{viewingUser.fullName}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{viewingUser.email}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Mobile Number:</span>
                  <span className="detail-value">{viewingUser.mobileNumber}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Role:</span>
                  <span className="detail-value">
                    <span className={`role-badge role-${viewingUser.role.toLowerCase().replace(/\s+/g, '-')}`}>
                      {viewingUser.role}
                    </span>
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status:</span>
                  <span className="detail-value">
                    <span className={`status-badge status-${viewingUser.status.toLowerCase()}`}>
                      {viewingUser.status}
                    </span>
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Last Login:</span>
                  <span className="detail-value">{viewingUser.lastLogin}</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && userToDelete && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete User</h2>
              <button className="modal-close" onClick={() => setShowDeleteConfirm(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete user <strong>{userToDelete.fullName}</strong> ({userToDelete.userId})?</p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
              <button className="btn-delete-confirm" onClick={confirmDeleteUser}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{successModalType === 'add' ? 'User Added' : 'User Updated'}</h2>
              <button className="modal-close" onClick={() => setShowSuccessModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>
                User <strong>{successUserName}</strong> has been {successModalType === 'add' ? 'added' : 'updated'} successfully!
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-save" onClick={() => setShowSuccessModal(false)}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Success Modal */}
      {showDeleteSuccess && deletedUserInfo && (
        <div className="modal-overlay" onClick={() => setShowDeleteSuccess(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>User Deleted</h2>
              <button className="modal-close" onClick={() => setShowDeleteSuccess(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>
                User <strong>{deletedUserInfo.fullName}</strong> ({deletedUserInfo.userId}) has been deleted successfully!
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-save" onClick={() => setShowDeleteSuccess(false)}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
